import * as React from 'react';
import PhoneInputPrimitive from 'react-phone-number-input/input';
import flags from 'react-phone-number-input/flags';
import type { Country, Value } from 'react-phone-number-input';
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
} from 'react-phone-number-input';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { ChevronsUpDown, Check } from 'lucide-react';

import 'react-phone-number-input/style.css';

function getCountryName(country: Country): string {
  try {
    return new Intl.DisplayNames(['fr'], { type: 'region' }).of(country) ?? country;
  } catch {
    return country;
  }
}

/** Prioritized countries shown at the top of the list */
const PRIORITY_COUNTRIES: Country[] = [
  'FR', 'BE', 'CH', 'CA', 'LU', 'MA', 'TN', 'SN', 'CI', 'CM', 'BJ',
];

function getSortedCountries(): Country[] {
  const all = getCountries();
  const rest = all.filter((c) => !PRIORITY_COUNTRIES.includes(c));
  rest.sort((a, b) => getCountryName(a).localeCompare(getCountryName(b), 'fr'));
  return [...PRIORITY_COUNTRIES, ...rest];
}

/**
 * Détecte le pays à partir d'un numéro E.164 (+33..., +212..., etc.)
 * Retourne le code pays ISO ou undefined si non reconnu.
 */
function detectCountryFromValue(value: string): Country | undefined {
  if (!value || !value.startsWith('+')) return undefined;
  try {
    const parsed = parsePhoneNumber(value);
    return parsed?.country;
  } catch {
    return undefined;
  }
}

/**
 * Normalise un numéro de téléphone brut (extrait d'un CV par ex.)
 * en supprimant espaces, tirets, points, parenthèses.
 * Conserve le + initial s'il existe.
 */
export function normalizePhoneNumber(raw: string): string {
  if (!raw) return '';
  return raw.replace(/[\s.\-()]/g, '');
}

interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  defaultCountry?: Country;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value,
      onChange,
      defaultCountry = 'FR',
      className,
      placeholder = '+33 6 12 34 56 78',
      disabled,
    },
    ref
  ) => {
    const [country, setCountry] = React.useState<Country>(defaultCountry);
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const searchRef = React.useRef<HTMLInputElement>(null);
    const hasAutoDetected = React.useRef(false);

    // Auto-détecte le pays à partir de la valeur initiale
    React.useEffect(() => {
      if (value && !hasAutoDetected.current) {
        const detected = detectCountryFromValue(value);
        if (detected) {
          setCountry(detected);
        }
        hasAutoDetected.current = true;
      }
    }, [value]);

    const sortedCountries = React.useMemo(() => getSortedCountries(), []);

    const filteredCountries = React.useMemo(() => {
      if (!search) return sortedCountries;
      const term = search.toLowerCase();
      return sortedCountries.filter(
        (c) =>
          getCountryName(c).toLowerCase().includes(term) ||
          c.toLowerCase().includes(term) ||
          `+${getCountryCallingCode(c)}`.includes(term)
      );
    }, [search, sortedCountries]);

    React.useEffect(() => {
      if (open) {
        setSearch('');
        setTimeout(() => searchRef.current?.focus(), 0);
      }
    }, [open]);

    const FlagComponent = flags[country];

    return (
      <div className={cn('flex gap-1', className)}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className="h-9 w-[80px] shrink-0 justify-between px-2"
            >
              <span className="flex items-center gap-1">
                {FlagComponent && (
                  <span className="inline-flex h-4 w-5 [&>svg]:h-full [&>svg]:w-full">
                    <FlagComponent title={country} />
                  </span>
                )}
                <span className="text-xs text-muted-foreground">
                  +{getCountryCallingCode(country)}
                </span>
              </span>
              <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[250px] p-0" align="start">
            <div className="p-2 border-b">
              <input
                ref={searchRef}
                type="text"
                placeholder="Rechercher un pays..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
            <div className="max-h-[200px] overflow-y-auto p-1">
              {filteredCountries.length === 0 && (
                <p className="text-sm text-muted-foreground p-2 text-center">
                  Aucun pays trouvé.
                </p>
              )}
              {filteredCountries.map((c, idx) => {
                const Flag = flags[c];
                const isPriorityBoundary =
                  !search && idx === PRIORITY_COUNTRIES.length;
                return (
                  <React.Fragment key={c}>
                    {isPriorityBoundary && (
                      <div className="my-1 h-px bg-border" />
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setCountry(c);
                        setOpen(false);
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground',
                        country === c && 'bg-accent'
                      )}
                    >
                      {Flag && (
                        <span className="inline-flex h-4 w-5 shrink-0 [&>svg]:h-full [&>svg]:w-full">
                          <Flag title={c} />
                        </span>
                      )}
                      <span className="truncate">{getCountryName(c)}</span>
                      <span className="ml-auto text-xs text-muted-foreground shrink-0">
                        +{getCountryCallingCode(c)}
                      </span>
                      {country === c && (
                        <Check className="h-4 w-4 text-primary shrink-0" />
                      )}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>

        <PhoneInputPrimitive
          ref={ref}
          country={country}
          international
          withCountryCallingCode
          value={(value as Value) || undefined}
          onChange={(val) => onChange?.(val || '')}
          placeholder={placeholder}
          disabled={disabled}
          inputMode="tel"
          className={cn(
            'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm'
          )}
        />
      </div>
    );
  }
);
PhoneInput.displayName = 'PhoneInput';

export { PhoneInput };
export type { PhoneInputProps };
